import { useState, type DragEvent, type ReactNode } from "react";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { STATUS_TONE, type CheckState, type Tone } from "@/lib/crm";
import { cn } from "@/lib/utils";

// Bouwstenen die op elk scherm terugkomen (vroeger de functies bovenaan views.js).
// Ze hangen niet af van FurniFlats-gegevens en zijn dus herbruikbaar in andere projecten.

/** Het FurniFlats-logo (cirkel met huis-F), in de kleur van de omliggende tekst. */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="24" cy="24" r="21" />
      <path d="M17 37V21l7.5-6.5L32 21M17 27.5h12" />
    </svg>
  );
}

/** Kop van een scherm: kleine bovenregel, titel, uitleg en knoppen rechts. */
export function PageHeader({ eyebrow, title, subtitle, actions }: { eyebrow: string; title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-[18px] flex flex-col gap-[18px] md:flex-row md:items-start md:justify-between">
      <div>
        <div className="text-[11px] font-extrabold uppercase tracking-[.11em] text-blush-ink">{eyebrow}</div>
        <h1 className="mt-[3px] text-[27px] font-bold leading-[1.18] tracking-[-.03em]">{title}</h1>
        {subtitle && <p className="mt-[7px] max-w-[790px] text-[13px] text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 md:justify-end">{actions}</div>}
    </header>
  );
}

/** Kaart met titel, uitleg, optioneel iets rechts in de kop, en inhoud. */
export function Section({ title, kicker, extra, children, className, bodyClassName }: { title: string; kicker?: string; extra?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {kicker && <CardDescription>{kicker}</CardDescription>}
        </div>
        {extra}
      </CardHeader>
      <CardContent className={bodyClassName}>{children}</CardContent>
    </Card>
  );
}

/** KPI-kaart: icoon, kleine toelichting rechts, groot getal en label. Klikbaar als `onClick` gegeven is. */
export function MetricCard({ icon: Icon, value, label, delta, onClick }: { icon?: LucideIcon; value: ReactNode; label: string; delta?: string; onClick?: () => void }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn("min-w-0 rounded-lg border bg-white p-3.5 text-left shadow-card", onClick && "cursor-pointer transition hover:-translate-y-px hover:border-line-strong")}
    >
      <div className="flex items-center justify-between gap-2">
        {Icon ? (
          <span className="grid size-[31px] place-items-center rounded-[9px] bg-surface-warm text-navy-800">
            <Icon className="size-[15px]" />
          </span>
        ) : (
          <span />
        )}
        <span className="text-right text-[11px] font-bold text-blush-ink">{delta}</span>
      </div>
      <div className="mt-3 text-[23px] font-extrabold tracking-[-.04em]">{value}</div>
      <div className="mt-px text-xs text-muted-foreground">{label}</div>
    </Comp>
  );
}

/** Badge met de kleur die bij een statuswaarde hoort. */
export function StatusBadge({ status, tone }: { status: string | undefined; tone?: Tone }) {
  if (!status) return <Badge>—</Badge>;
  return <Badge tone={tone ?? STATUS_TONE[status] ?? "neutral"}>{status}</Badge>;
}

export function EmptyState({ icon: Icon, title, text }: { icon?: LucideIcon; title: string; text?: string }) {
  return (
    <div className="px-5 py-[30px] text-center text-muted-foreground">
      {Icon && <Icon className="mx-auto size-[26px]" />}
      <h3 className="mt-2 text-sm font-semibold text-foreground">{title}</h3>
      {text && <p className="mx-auto mt-1.5 max-w-[410px] text-xs">{text}</p>}
    </div>
  );
}

/** Eén regel in een lijst: naam + onderregel, twee kolommen, en iets rechts (standaard een pijltje). */
export function RecordRow({
  title,
  sub,
  cell1,
  cell1Sub,
  cell2,
  cell2Sub,
  end,
  onClick,
  className
}: {
  title: ReactNode;
  sub?: ReactNode;
  cell1?: ReactNode;
  cell1Sub?: ReactNode;
  cell2?: ReactNode;
  cell2Sub?: ReactNode;
  end?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick()) : undefined}
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-[11px] border bg-white px-3 py-2.5 md:grid-cols-[minmax(0,1.3fr)_minmax(100px,.8fr)_minmax(100px,.75fr)_auto]",
        onClick && "cursor-pointer transition hover:border-line-strong hover:shadow-card",
        className
      )}
    >
      <div className="min-w-0">
        <strong className="block truncate text-[13px]">{title}</strong>
        {sub && <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{sub}</span>}
      </div>
      <div className="hidden min-w-0 text-xs md:block">
        {cell1}
        {cell1Sub && <small className="mt-0.5 block truncate text-[11px] text-muted-foreground">{cell1Sub}</small>}
      </div>
      <div className="hidden min-w-0 text-xs md:block">
        {cell2}
        {cell2Sub && <small className="mt-0.5 block truncate text-[11px] text-muted-foreground">{cell2Sub}</small>}
      </div>
      <div>{end === undefined ? <ChevronRight className="size-[15px] text-muted-foreground" /> : end}</div>
    </div>
  );
}

export interface TimelineEntry {
  time: string;
  title: string;
  meta?: string;
  owner?: string;
}

/** Verticale tijdlijn (afspraken, contactmomenten). */
export function Timeline({ items, empty }: { items: TimelineEntry[]; empty?: ReactNode }) {
  if (!items.length) return <>{empty}</>;
  return (
    <div className="grid">
      {items.map((item, i) => (
        <div key={i} className="relative grid min-h-[52px] grid-cols-[58px_16px_minmax(0,1fr)_auto] gap-2">
          <div className="pt-0.5 text-[11px] tabular-nums text-muted-foreground">{item.time}</div>
          <div className="relative">
            <span className={cn("absolute left-[7px] top-0 w-px bg-border", i === items.length - 1 ? "bottom-6" : "bottom-0")} />
            <span className="absolute left-[3px] top-1 size-[9px] rounded-full border-2 border-white bg-navy-800 shadow-[0_0_0_1px_hsl(var(--border))]" />
          </div>
          <div className="min-w-0 pb-[15px]">
            <div className="text-[13px] font-semibold">{item.title}</div>
            {item.meta && <div className="mt-0.5 text-[11px] text-muted-foreground">{item.meta}</div>}
          </div>
          {item.owner ? <span className="self-start rounded-full bg-tone-blue-soft px-2 py-[3px] text-[10px] font-bold text-tone-blue">{item.owner}</span> : <span />}
        </div>
      ))}
    </div>
  );
}

/** Melding in een kader: geel (standaard), blush of rood. */
export function Notice({ title, children, tone = "yellow", className }: { title?: string; children?: ReactNode; tone?: "yellow" | "blush" | "red"; className?: string }) {
  const tones = {
    yellow: "border-[#ecd8b0] bg-tone-yellow-soft text-tone-yellow",
    blush: "border-blush bg-blush-soft text-navy-800",
    red: "border-[#ebc7c3] bg-tone-red-soft text-tone-red"
  };
  return (
    <div className={cn("rounded-[10px] border px-[11px] py-2.5 text-xs", tones[tone], className)}>
      {title && <strong className="mb-0.5 block">{title}</strong>}
      {children}
    </div>
  );
}

/** Regel in een checklist: vinkje (groen), uitroepteken (geel) of kruis (rood), met uitleg en rechts een label. */
export function CheckRow({ state, title, detail, weight }: { state: CheckState; title: string; detail?: string; weight?: string }) {
  const icon = { ok: "✓", warn: "!", missing: "×" }[state];
  const tone = { ok: "bg-tone-green-soft text-tone-green", warn: "bg-tone-yellow-soft text-tone-yellow", missing: "bg-tone-red-soft text-tone-red" }[state];
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-[9px] rounded-[9px] bg-surface-soft px-2.5 py-[9px]">
      <span className={cn("grid size-[22px] place-items-center rounded-[7px] text-[11px] font-extrabold", tone)} aria-hidden="true">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-xs font-semibold">{title}</div>
        {detail && <div className="mt-px text-[11px] text-muted-foreground">{detail}</div>}
      </div>
      <span className="text-[11px] text-muted-foreground">{weight}</span>
    </div>
  );
}

/** Stappen van een flow: afgewerkt (navy), huidig (blush), nog te doen. */
export function Stepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="flex items-start overflow-x-auto pb-1 pt-1">
      {steps.map((label, i) => {
        const state = i < current ? "complete" : i === current ? "current" : "todo";
        return (
          <li key={label} className="relative min-w-[96px] flex-1 text-center" aria-current={state === "current" ? "step" : undefined}>
            {i < steps.length - 1 && <span className={cn("absolute left-[calc(50%+15px)] right-[calc(-50%+15px)] top-3.5 h-0.5", state === "complete" ? "bg-navy-800" : "bg-border")} />}
            <span
              className={cn(
                "relative z-[1] mx-auto grid size-[29px] place-items-center rounded-full border-2 text-[11px] font-extrabold",
                state === "complete" && "border-navy-800 bg-navy-800 text-white",
                state === "current" && "border-blush-strong bg-blush text-navy-950 shadow-[0_0_0_5px] shadow-blush-soft",
                state === "todo" && "border-line-strong bg-white text-muted-foreground"
              )}
            >
              {state === "complete" ? "✓" : i + 1}
            </span>
            <span className={cn("mt-2 block text-[11px]", state === "todo" ? "text-muted-foreground" : "font-bold")}>{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** Rij met filterknoppen (tabs van een lijst). */
export function FilterChips<T extends string>({ options, value, onChange, className }: { options: { value: T; label: string }[]; value: T; onChange: (value: T) => void; className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={cn(
            "min-h-[30px] rounded-full border bg-white px-[11px] py-[5px] text-[11px] font-semibold text-muted-foreground hover:border-line-strong hover:text-foreground",
            o.value === value && "border-navy-800 bg-navy-800 text-white hover:border-navy-800 hover:text-white"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Raster van label + waarde (gegevens van een record). */
export function InfoGrid({ items }: { items: [string, ReactNode][] }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0 rounded-[9px] bg-surface-soft p-2.5">
          <span className="block text-[10px] uppercase tracking-[.06em] text-muted-foreground">{label}</span>
          <strong className="mt-[3px] block break-words text-xs">{value || "—"}</strong>
        </div>
      ))}
    </div>
  );
}

/** Ring met een percentage (FurniFlats-score, huurperiode). */
export function Ring({ value, label, size = "lg", color = "#2a3760" }: { value: number; label?: string; size?: "lg" | "sm"; color?: string }) {
  const lg = size === "lg";
  return (
    <span className={cn("grid shrink-0 place-items-center rounded-full", lg ? "size-[78px]" : "size-[30px]")} style={{ background: `conic-gradient(${color} ${value}%, #ece3df 0)` }} role="img" aria-label={`${value}%`}>
      <span className={cn("col-start-1 row-start-1 rounded-full bg-white", lg ? "size-[60px]" : "size-[22px]")} />
      {lg && <strong className="relative col-start-1 row-start-1 text-[17px] tracking-[-.04em]">{label ?? `${value}%`}</strong>}
    </span>
  );
}

/** Regel in een actielijst: titel + onderregel, eigenaar en termijn rechts, en eventueel knoppen. */
export function ActionRow({ title, sub, owner, due, dueClassName, children }: { title: string; sub?: string; owner?: string; due?: string; dueClassName?: string; children?: ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-[9px] border bg-white px-2.5 py-[9px] sm:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
      <div className="min-w-0">
        <div className="text-xs font-semibold">{title}</div>
        {sub && <div className="mt-px truncate text-[11px] text-muted-foreground">{sub}</div>}
      </div>
      {owner ? <span className="hidden rounded-full bg-tone-blue-soft px-2 py-[3px] text-[10px] font-bold text-tone-blue sm:inline">{owner}</span> : <span className="hidden sm:inline" />}
      <span className={cn("whitespace-nowrap text-[11px] text-muted-foreground", dueClassName)}>{due}</span>
      {children ? <span className="col-span-2 flex items-center gap-1 sm:col-span-1">{children}</span> : <span className="hidden sm:inline" />}
    </div>
  );
}

/** Kolommen naast elkaar (pipeline, werkweek), horizontaal scrollbaar op een smal scherm. */
export function Kanban({ children, columns = 5 }: { children: ReactNode; columns?: number }) {
  return (
    <div className="grid gap-2.5 overflow-x-auto pb-1" style={{ gridTemplateColumns: `repeat(${columns}, minmax(190px, 1fr))` }}>
      {children}
    </div>
  );
}

/** Type in dataTransfer voor een versleepte kaart (kleine letters: browsers zetten types om). */
const KANBAN_DRAG_TYPE = "application/x-kanban-card";

/**
 * Kolom van een bord. Met `onDropCard` kan je er kaarten (met een `dragId`) naartoe slepen; ze komen
 * dan onderaan, tenzij een kaart zelf de drop afhandelt (`KanbanCard` met `onDropCard`).
 * `footer` staat onder de kaarten (bv. een knop "Kaart toevoegen").
 */
export function KanbanColumn({
  title,
  count,
  highlight,
  children,
  empty = "Leeg",
  onDropCard,
  footer
}: {
  title: string;
  count: number;
  highlight?: boolean;
  children: ReactNode;
  empty?: string;
  onDropCard?: (id: string) => void;
  footer?: ReactNode;
}) {
  const [over, setOver] = useState(false);
  const accepts = (e: DragEvent) => Boolean(onDropCard) && e.dataTransfer.types.includes(KANBAN_DRAG_TYPE);
  return (
    <section
      onDragOver={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setOver(false)}
      onDrop={(e) => {
        if (!accepts(e)) return;
        setOver(false);
        // Al afgehandeld door een kaart in deze kolom (neerzetten vóór die kaart).
        if (e.defaultPrevented) return;
        e.preventDefault();
        onDropCard!(e.dataTransfer.getData(KANBAN_DRAG_TYPE));
      }}
      className={cn("min-w-[190px] rounded-xl border bg-[#f6efec] p-[9px] transition-colors", highlight && "border-blush-strong bg-blush-soft", over && "border-dashed border-navy-800 bg-blush-soft")}
    >
      <div className="flex items-center justify-between gap-2 px-[3px] pb-[9px] pt-[3px]">
        <span className="text-xs font-bold">{title}</span>
        <span className="grid h-5 min-w-[22px] place-items-center rounded-[7px] border bg-white px-[5px] text-[10px]">{count}</span>
      </div>
      <div className="grid gap-[7px]">{count ? children : <div className="p-2.5 text-center text-[11px] text-muted-foreground">{empty}</div>}</div>
      {footer && <div className="mt-[7px]">{footer}</div>}
    </section>
  );
}

/**
 * Kaart op een bord. Met `dragId` kan je de kaart naar een andere kolom slepen. Met `onDropCard`
 * kan je een andere kaart vóór deze kaart neerzetten (een lijntje bovenaan toont waar).
 */
export function KanbanCard({
  title,
  sub,
  footer,
  current,
  onClick,
  dragId,
  onDropCard
}: {
  title: string;
  sub?: string;
  footer?: ReactNode;
  current?: boolean;
  onClick?: () => void;
  dragId?: string;
  onDropCard?: (id: string) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [over, setOver] = useState(false);
  const accepts = (e: DragEvent) => Boolean(onDropCard) && e.dataTransfer.types.includes(KANBAN_DRAG_TYPE);
  return (
    <article
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick()) : undefined}
      draggable={dragId ? true : undefined}
      onDragStart={
        dragId
          ? (e) => {
              e.dataTransfer.setData(KANBAN_DRAG_TYPE, dragId);
              e.dataTransfer.effectAllowed = "move";
              setDragging(true);
            }
          : undefined
      }
      onDragEnd={dragId ? () => setDragging(false) : undefined}
      onDragOver={(e) => {
        if (!accepts(e) || dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setOver(false)}
      onDrop={(e) => {
        if (!accepts(e)) return;
        setOver(false);
        // preventDefault: de kolom eronder ziet dat de drop al afgehandeld is.
        e.preventDefault();
        const id = e.dataTransfer.getData(KANBAN_DRAG_TYPE);
        if (id && id !== dragId) onDropCard!(id);
      }}
      aria-current={current || undefined}
      className={cn(
        "rounded-[9px] border bg-white p-2.5 shadow-[0_1px_2px_rgba(20,28,51,.03)]",
        onClick && "cursor-pointer hover:border-line-strong",
        dragId && "cursor-grab active:cursor-grabbing",
        dragging && "opacity-50",
        current && "border-navy-800 shadow-[0_0_0_2px] shadow-blush",
        over && "shadow-[0_-3px_0_0] shadow-navy-800"
      )}
    >
      <div className="text-xs font-bold">{title}</div>
      {sub && <div className="mt-[3px] text-[11px] text-muted-foreground">{sub}</div>}
      {footer && <div className="mt-[9px] flex items-center justify-between gap-1.5">{footer}</div>}
    </article>
  );
}

/** Ronde badge met initialen (medewerker). */
export function Avatar({ name }: { name?: string }) {
  const initials =
    (name ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]!.toUpperCase())
      .join("") || "?";
  return (
    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-blush text-[10px] font-extrabold text-navy-950" title={name}>
      {initials}
    </span>
  );
}

/** Klasse voor knoppen op de donkere kop van een dossier. */
export const heroButton = "border-white/20 bg-white/[.08] text-white shadow-none hover:bg-white/[.15] hover:border-white/20";

/** Donkere kop van een dossier (pand, bedrijf): type, titel, onderregel, knoppen en vier kerncijfers. */
export function RecordHero({ type, title, subtitle, actions, meta }: { type: string; title: string; subtitle?: string; actions?: ReactNode; meta: [string, ReactNode][] }) {
  return (
    <section className="mb-3.5 rounded-[18px] bg-[linear-gradient(135deg,#141c33,#2a3760)] p-[17px] text-white shadow-pop">
      <div className="flex flex-col gap-[18px] lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-extrabold uppercase tracking-[.11em] text-blush">{type}</div>
          <h1 className="mt-[3px] text-[23px] font-bold leading-[1.2] tracking-[-.03em]">{title}</h1>
          {subtitle && <div className="mt-[5px] text-xs text-white/65">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap gap-[7px] lg:justify-end">{actions}</div>}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        {meta.map(([label, value]) => (
          <div key={label} className="min-w-0 rounded-[10px] border border-white/[.08] bg-white/[.075] px-2.5 py-[9px]">
            <span className="block text-[9px] uppercase tracking-[.07em] text-white/50">{label}</span>
            <strong className="mt-[3px] block truncate text-xs">{value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}
